import React from 'react';
import PropTypes from 'prop-types';
import loadable from 'react-loadable';
import { compose, setStatic } from 'recompose';
import { load as loadLocation } from '../stores/locations';
import { load as loadFormBootstrap } from '../stores/forms/seasonTickets';
import { loadListing } from '../stores/forms/checkout';
import { getConsents } from '../stores/consents';
import { load as loadAuth } from '../stores/auth';
import { loadPartnerWhiteLabel } from '../stores/brand';

const Loading = props => {
  if (props.error) {
    // Don't swallow the error
    console.error(props.error);

    return (
      <div className="p-loading">
        Error!{' '}
        <button type="button" onClick={props.retry}>
          Retry
        </button>
      </div>
    );
  }
  if (props.pastDelay) {
    return <div className="p-loading">Loading...</div>;
  }
  return null;
};

Loading.propTypes = {
  error: PropTypes.object,
  retry: PropTypes.func.isRequired,
  pastDelay: PropTypes.bool.isRequired
};

Loading.defaultProps = {
  error: false
};

export const Home = loadable({
  loader: () => import(/* webpackChunkName: "page-home" */ './Home'),
  loading: Loading
});

export const Landing = compose(
  setStatic('fetchData', (store, route) =>
    store.dispatch(loadLocation(route.params.slug, route.params.country))
  )
)(
  loadable({
    loader: () => import(/* webpackChunkName: "page-landing" */ './Landing'),
    loading: Loading
  })
);

export const LoggedInPage = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-logged-in" */ './LoggedInPage'),
  loading: Loading
});

export const SeasonTicket = compose(
  setStatic('fetchData', (store, match) =>
    store.dispatch(loadFormBootstrap(match.params.vendor))
  )
)(
  loadable({
    loader: () =>
      import(/* webpackChunkName: "page-season-ticket" */ './SeasonTicket'),
    loading: Loading
  })
);

export const SearchResults = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-search-results" */ './SearchResults'),
  loading: Loading
});

export const PartnerSearchResults = compose(
  setStatic('fetchData', (store, match) => {
    const { partnerName } = match.params;

    return store.dispatch(loadPartnerWhiteLabel(partnerName));
  })
)(
  loadable({
    loader: () =>
      import(/* webpackChunkName: "page-search-results" */ './PartnerSearchResults'),
    loading: Loading
  })
);

export const LoginWall = compose(
  setStatic('fetchData', (store, match, queryParams) =>
    store.dispatch(getConsents(queryParams, false, match))
  )
)(
  loadable({
    loader: () =>
      import(/* webpackChunkName: "page-login-wall" */ './LoginWall'),
    loading: Loading
  })
);

export const Register = loadable({
  loader: () => import(/* webpackChunkName: "page-register" */ './Register'),
  loading: Loading
});

export const Checkout = compose(
  setStatic('fetchData', (store, match) => {
    const listingId = parseInt(match.params.listingId, 10);
    return store.dispatch(loadListing(listingId));
  })
)(
  loadable({
    loader: () => import(/* webpackChunkName: "page-checkout" */ './Checkout'),
    loading: Loading
  })
);

export const PartnerCheckout = compose(
  setStatic('fetchData', (store, match) => {
    const listingId = parseInt(match.params.listingId, 10);
    const { partnerName } = match.params;

    const whiteLabel = store.dispatch(loadPartnerWhiteLabel(partnerName));
    const listing = store.dispatch(loadListing(listingId));

    return Promise.all([whiteLabel, listing]);
  })
)(
  loadable({
    loader: () =>
      import(/* webpackChunkName: "page-checkout" */ './PartnerCheckout'),
    loading: Loading
  })
);

export const ConsentsWall = compose(
  setStatic('fetchData', (store, match, queryParams) =>
    store.dispatch(getConsents(queryParams, false, match))
  )
)(
  loadable({
    loader: () =>
      import(/* webpackChunkName: "page-consent" */ './ConsentWall'),
    loading: Loading
  })
);

export const Revoke = compose(
  setStatic('fetchData', store =>
    store.dispatch(loadAuth()).then(() => store.dispatch(getConsents()))
  )
)(
  loadable({
    loader: () => import(/* webpackChunkName: "page-revoke" */ './Revoke'),
    loading: Loading
  })
);

export const AddListing = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-add-listing" */ './dashboard/AddListing'),
  loading: Loading
});

export const EditListing = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-edit-listing" */ './dashboard/EditListing'),
  loading: Loading
});

export const UpdatePrompt = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-update-prompt" */ './dashboard/UpdatePrompt'),
  loading: Loading
});

export const ParkingSpaces = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-parking-spaces" */ './dashboard/ParkingSpaces'),
  loading: Loading
});

export const Availability = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-availability" */ './dashboard/Availability'),
  loading: Loading
});

export const ReferFriend = loadable({
  loader: () => import(/* webpackChunkName: "page-refer" */ './ReferFriend'),
  loading: Loading
});

export const VehiclePage = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-vehicle" */ './dashboard/VehiclePage'),
  loading: Loading
});

export const PaymentsPage = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-payments" */ './dashboard/PaymentsPage'),
  loading: Loading
});

export const WithdrawalPage = loadable({
  loader: () =>
    import(/* webpackChunkName: "page-withdrawal" */ './dashboard/WithdrawalPage'),
  loading: Loading
});
